import React, { useState } from 'react';
import { AppBundle } from 'api/optimalprint-sdk.d';
import { useDispatch, useSelector } from 'react-redux';
import getSelectedBundleProducts from 'store/product/selector/getSelectedBundleProducts';
import { deselectBundleProductAction, selectBundleProductAction } from 'store/product/slice';
import forceProductPreviewFromBundleProductsOperation from 'store/product/operation/forceProductPreviewFromBundleProductsOperation';
import getSelectedBundleItems from 'utils/bundles/getSelectedBundleItems';
import getCategoryId from 'store/category/selector/getCategoryId';
import { useTranslation } from 'react-i18next';
import EllipsisIcon from 'components/Common/BreadcrumbsMobile/EllipsisIcon';
import { Price } from 'components/Common';
import OpCarousel from 'components/Common/op-ui-wrappers/OpCarousel/OpCarousel';
import OpMountableFrame from 'components/Common/op-ui-wrappers/OpMountableFrame/OpMountableFrame';
import getProductItem from 'store/product/selector/getProductItem';
import BundleProductItem from '../BundleProductItem/BundleProductItem';
import styles from './BundledProductsGroup.module.scss';

const DEFAULT_VISIBLE_DESCRIPTION_SIZE = 140;

type ProductItem = AppBundle.Api.Entity.Design.V1.DesignAbstractProduct.DesignProductItem.IProductItem;

interface Props {
  bundleGroup: AppBundle.Api.Entity.Design.V1.DesignAbstractProduct.DesignProductItem.IBundleGroup;
  showDescription?: boolean;
  theme?: string;
}

const BundledProductsGroup = ({ bundleGroup, theme, showDescription = true }: Props) => {
  const dispatch = useDispatch();
  const productUid = useSelector(getProductItem)?.product.productUid;
  const { t } = useTranslation();
  const allSelectedProducts = useSelector(getSelectedBundleProducts);
  const categoryId = useSelector(getCategoryId) || 1;
  const [showWholeDescription, setShowWholeDescription] = useState(false);

  const selectedProducts = allSelectedProducts
    .filter((sp) => sp.bundleGroupId === bundleGroup.bundleGroupId)
    .map((sp) => sp.productId);
  if (selectedProducts.length === 0) {
    // preselect none option
    selectedProducts.push(0);
  }
  const selectedItems = getSelectedBundleItems(bundleGroup, selectedProducts);
  const selectedItem = selectedItems ? selectedItems[0] : undefined;

  const toggleBundleProduct = (productItem: ProductItem) => {
    const isSelected = selectedProducts.indexOf(productItem.productId) !== -1;
    if (!isSelected) {
      dispatch(
        selectBundleProductAction({
          bundleGroupId: bundleGroup.bundleGroupId,
          useMultipleSelection: bundleGroup.canSelectMultiple,
          productItem,
          categoryId,
        }),
      );
    } else {
      dispatch(
        deselectBundleProductAction({
          bundleGroupId: bundleGroup.bundleGroupId,
          productItem,
        }),
      );
    }
    dispatch(forceProductPreviewFromBundleProductsOperation());
  };

  const expandDescription = () => {
    setShowWholeDescription(true);
  };

  const displayFrameDescription = () => {
    if (!selectedItem || (selectedItem && selectedItem.productId === 0)) {
      return null;
    }
    const description = selectedItem?.productDescription;
    const showMoreButton = Number(description?.length) > DEFAULT_VISIBLE_DESCRIPTION_SIZE && !showWholeDescription;
    const cutPosition = showMoreButton
      ? description?.indexOf(' ', DEFAULT_VISIBLE_DESCRIPTION_SIZE)
      : description?.length;
    const descriptionToShow = description?.slice(0, cutPosition);

    return (
      <div className={styles.selectedItemDetails}>
        <div className={styles.description} onClick={expandDescription}>
          {descriptionToShow}
          {showMoreButton && (
            <>
              &nbsp;
              <EllipsisIcon />
            </>
          )}
        </div>
        <OpMountableFrame product-uid={productUid} bundled-product-uid={selectedItem.productUid} />
      </div>
    );
  };

  return (
    <div className={styles.bundleGroup}>
      <div className={styles.groupTitle}>
        {!selectedItem && bundleGroup.label}
        {Boolean(selectedItem && selectedItem.productId === 0) && bundleGroup.label}
        {Boolean(selectedItem && selectedItem.productId !== 0) && (
          <div>
            {selectedItem?.name}
            <Price prefix=" +" categoryId={categoryId} productId={Number(selectedItem?.productId)} />
          </div>
        )}
      </div>
      <OpCarousel variant="frames-selector">
        {bundleGroup.items?.map((item, index) => (
          <BundleProductItem
            key={`bpi${index}`}
            bundleGroupId={bundleGroup.bundleGroupId}
            item={item}
            selected={selectedProducts.indexOf(item.productId) !== -1}
            onClick={toggleBundleProduct}
          />
        ))}
      </OpCarousel>
      {showDescription && displayFrameDescription()}
    </div>
  );
};

export default BundledProductsGroup;
